import React, { memo } from 'react';

import cx from 'classnames';

import { useQuestionCommands } from '../../commands/CustomizationCommandsContext';
import { Badge } from '../badges/Badge';
import { SortableRow, type SortableRowSlots } from '../dnd/SortableRow';
import type { QuestionRowViewModel } from '../../viewmodels/createQuestionRowViewModel';
import { InlineRetry } from './InlineRetry';
import { QuestionActionsMenu } from './QuestionActionsMenu';
import { ResponseTypeCell } from './ResponseTypeCell';
import { RowSpinner } from './RowSpinner';

import styles from './QuestionRow.scss';

type Props = {
  viewModel: QuestionRowViewModel;
};

type ContentProps = Props & {
  sortable?: SortableRowSlots;
};

function QuestionRowContent({ viewModel, sortable }: ContentProps) {
  const { selectEntry, retry } = useQuestionCommands();
  const { id, spinner, failure, menuEntries } = viewModel;
  const hasMenu = !spinner && menuEntries.length > 0;
  const hasActions = Boolean(spinner) || hasMenu;

  return (
    <div
      role="row"
      ref={sortable?.rowRef}
      style={sortable?.rowStyle}
      className={styles.row}
      data-test="question-row"
    >
      <div role="cell" className={styles.questionCell}>
        {sortable?.dragHandle}
        {viewModel.badges.map(badge => (
          <Badge key={badge.kind} viewModel={badge} />
        ))}
        <span className={cx(viewModel.isHidden && styles.hidden)}>
          {viewModel.label}
        </span>
      </div>
      <div role="cell" className={styles.responseCell}>
        <ResponseTypeCell viewModel={viewModel.responseType} />
        {hasActions && (
          <div className={styles.actions}>
            {spinner && <RowSpinner viewModel={spinner} />}
            {hasMenu && (
              <QuestionActionsMenu
                viewModel={viewModel}
                onSelect={entryId => selectEntry(id, entryId)}
              />
            )}
          </div>
        )}
      </div>
      {failure && (
        <div role="cell" className={styles.failureCell}>
          <InlineRetry viewModel={failure} onRetry={() => retry(id)} />
        </div>
      )}
    </div>
  );
}

export const QuestionRow = memo(function QuestionRow(props: Props) {
  const { id, reorder } = props.viewModel;

  if (!reorder.canReorder) {
    return <QuestionRowContent {...props} />;
  }

  return (
    <SortableRow id={id} reorder={reorder}>
      {sortable => <QuestionRowContent {...props} sortable={sortable} />}
    </SortableRow>
  );
});
